import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ConfigSwitchPanel } from 'src/app/components/ConfigSwitchSection';
import { getAgencyConfig, updateAgencyConfig } from 'src/features/agencySlice';

export const AGENCY_CONFIG_SECTIONS = [
  {
    id: 'customer',
    title: 'Khách hàng',
    items: [
      {
        key: 'allow_edit_customer',
        label: 'Sửa thông tin khách hàng'
      }
    ]
  },
  {
    id: 'device',
    title: 'Thiết bị',
    items: [
      {
        key: 'is_allow_swap',
        label: 'Đổi / thay thế thiết bị'
      },
      {
        key: 'is_allow_swap_sub_agency',
        label: 'Chuyển kho thiết bị'
      }
    ]
  }
];

function AgencyConfigModal({ open, onClose, agencySelected }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const agencyId = agencySelected?.id;

  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingKeys, setSavingKeys] = useState([]);

  useEffect(() => {
    if (!open || !agencyId) return;
    let active = true;
    setLoading(true);
    setError('');
    dispatch(getAgencyConfig(agencyId)).then(action => {
      if (!active) return;
      if (getAgencyConfig.fulfilled.match(action)) {
        const { agencyId: _id, ...payload } = action.payload;
        setConfig(payload);
      } else {
        setError(action.payload?.message || 'Lấy cấu hình đại lý thất bại');
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [open, agencyId, dispatch]);

  // Gạt switch nào gửi đúng cờ đó (partial update), dùng payload trả về làm nguồn sự thật
  const handleToggle = (key, value) => {
    setSavingKeys(prev => [...prev, key]);
    dispatch(updateAgencyConfig({ agencyId, config: { [key]: value } })).then(
      action => {
        if (updateAgencyConfig.fulfilled.match(action)) {
          const { agencyId: _id, ...payload } = action.payload;
          setConfig(prev => ({ ...prev, ...payload }));
        }
        setSavingKeys(prev => prev.filter(k => k !== key));
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box flex={1} minWidth={0}>
          <Typography className={classes.title}>Cấu hình quyền hạn</Typography>
          {agencySelected?.name && (
            <Typography className={classes.subtitle} noWrap>
              Đại lý: <b>{agencySelected.name}</b>
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" className={classes.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} style={{ color: '#D9042F' }} />
          </Box>
        ) : error ? (
          <Box className={classes.errorBox}>{error}</Box>
        ) : (
          <ConfigSwitchPanel
            sections={AGENCY_CONFIG_SECTIONS}
            values={config}
            savingKeys={savingKeys}
            onToggle={handleToggle}
          />
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} variant="contained" disableElevation className={classes.closeBtn}>
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 14,
    overflow: 'hidden'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #eef0f2'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1f2329',
    lineHeight: 1.3
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
    '& b': {
      color: '#1f2329',
      fontWeight: 600
    }
  },
  closeButton: {
    color: '#6b7280',
    marginLeft: 8,
    '&:hover': {
      backgroundColor: '#f2f3f5'
    }
  },
  dialogContent: {
    padding: '20px !important',
    backgroundColor: '#f7f8fa'
  },
  errorBox: {
    padding: '16px',
    borderRadius: 10,
    textAlign: 'center',
    fontSize: 14,
    color: '#b42318',
    backgroundColor: '#fef3f2',
    border: '1px solid #fecdca'
  },
  dialogActions: {
    padding: '12px 20px',
    borderTop: '1px solid #eef0f2'
  },
  closeBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: '6px 20px',
    color: '#fff',
    backgroundColor: '#D9042F',
    '&:hover': {
      backgroundColor: '#b8032a'
    }
  }
}));

export default AgencyConfigModal;
